import { Badge, Box, Flex, HStack, Icon, Text, VStack, Avatar, Divider } from '@chakra-ui/react';
import { djs } from '@duke-hq/djs';
import { Pin } from 'lucide-react';
import { UserAvatar } from '../UserAvatar';
import { NoteInterface } from '../types';
import { NoteMetadata } from './NoteMetadata';
import { NoteActions } from './NoteActions';
import { Trans } from '@lingui/react/macro';

export function PinnedNote({
  with_user_drn,
  about_user_drn,
  outcome,
  note_type,
  date_time,
  createdBy,
  text,
  category,
  id,
  users,
}: NoteInterface) {
  const withUser = with_user_drn !== null && users ? users[with_user_drn] : null;
  const aboutUser = about_user_drn !== null && users ? users[about_user_drn] : null;
  const authorFullName = createdBy ? `${createdBy.first_name} ${createdBy?.last_name}` : '';

  return (
    <Box as="section" width="full">
      <Flex
        borderRadius="lg"
        bg="bg.surface"
        borderWidth={1}
        borderColor="bg.accent.inverse-default"
      >
        <VStack spacing="3" align="left" m="4" w="full">
          <HStack
            justifyContent="space-between"
            whiteSpace="nowrap"
            flexWrap="wrap"
            position="relative"
            pr="34px"
            minH="34px"
            gap={2}
          >
            <HStack>
              <Badge
                backgroundColor="bg.accent.inverse-muted"
                borderRadius="full"
                color="fg.inverse"
                display="inline-flex"
                alignItems="center"
                gap={1.5}
                p={2}
                px={4}
                textTransform="none"
              >
                <Trans>Pinned Note</Trans>
                <Icon as={Pin} />
              </Badge>
              <Text textStyle="xs" color="fg.emphasis">
                {djs(date_time).format('LLL')}
              </Text>
            </HStack>
            <HStack justifyContent="space-between">
              <HStack flexWrap="wrap">
                <HStack>
                  <Avatar size="xs" name={authorFullName} src={undefined} />
                  <Text fontSize="xs" fontWeight="medium">
                    {authorFullName}
                  </Text>
                </HStack>
                <UserAvatar
                  drn={with_user_drn}
                  label="With"
                  user={{
                    ...withUser,
                    displayName: withUser?.displayName ?? undefined,
                    avatar: withUser?.avatar ?? undefined,
                    role: withUser?.role ?? undefined,
                  }}
                />
                {with_user_drn !== about_user_drn ? (
                  <UserAvatar
                    drn={about_user_drn}
                    label="About"
                    user={{
                      ...aboutUser,
                      displayName: aboutUser?.displayName ?? undefined,
                      avatar: aboutUser?.avatar ?? undefined,
                      role: aboutUser?.role ?? undefined,
                    }}
                  />
                ) : null}
              </HStack>
              {id ? (
                <Box position="absolute" right={0} top={0}>
                  <NoteActions noteId={id} isPinned={true} />
                </Box>
              ) : null}
            </HStack>
          </HStack>

          <NoteMetadata noteType={note_type} category={category} outcome={outcome} />

          <Divider my="1" borderColor="bg.muted" />

          <Box
            w="full"
            color="fg.muted"
            lineHeight="tall"
            fontSize="sm"
            wordBreak="break-word"
            whiteSpace="pre-wrap"
          >
            {text}
          </Box>
        </VStack>
      </Flex>
    </Box>
  );
}
